import React, { useState, useEffect } from "react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { MultiSelect } from "@/components/MultiSelect/MultiSelect";
import SearchInput from "@/components/SearchInput/SearchInput";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SlidersHorizontal } from "lucide-react";
import { EventFilterMetadataItem } from "../../slices/eventListSlice";

interface EventFiltersProps {
  filters: {
    searchTerm: string;
    tradeIds: string[];
    eventTypeIds: string[];
    businessRoleIds: string[];
    categoryIds: string[];
    tagIds: string[];
  };
  onFilterChange: (filterKey: string, value: string | string[]) => void;
  eventTypes: EventFilterMetadataItem[];
  eventCategories: EventFilterMetadataItem[];
  businessRoles: EventFilterMetadataItem[];
  trades: EventFilterMetadataItem[];
  eventTags: EventFilterMetadataItem[];
}

const EventFilters: React.FC<EventFiltersProps> = ({
  filters,
  onFilterChange,
  eventTypes,
  eventCategories,
  businessRoles,
  trades,
  eventTags,
}) => {
  const [searchInput, setSearchInput] = useState(filters.searchTerm);
  const debouncedSearch = useDebouncedValue(searchInput, 500);

  useEffect(() => {
    if (debouncedSearch !== filters.searchTerm) {
      onFilterChange("searchTerm", debouncedSearch);
    }
  }, [debouncedSearch, filters.searchTerm, onFilterChange]);

  const typeOptions = eventTypes.map((et) => ({
    label: et.name,
    value: String(et.id),
  }));
  const categoryOptions = eventCategories.map((c) => ({
    label: c.name,
    value: String(c.id),
  }));
  const tradeOptions = trades.map((t) => ({
    label: t.name,
    value: String(t.id),
  }));
  const roleOptions = businessRoles.map((r) => ({
    label: r.name,
    value: String(r.id),
  }));
  const tagOptions = eventTags.map((tg) => ({
    label: tg.name,
    value: String(tg.id),
  }));

  // Count active filter selections so the Filters button can show a badge.
  const activeFilterCount =
    filters.eventTypeIds.length +
    filters.categoryIds.length +
    filters.tradeIds.length +
    filters.businessRoleIds.length +
    filters.tagIds.length;

  const clearAll = () => {
    if (filters.eventTypeIds.length > 0) onFilterChange("eventTypeIds", []);
    if (filters.categoryIds.length > 0) onFilterChange("categoryIds", []);
    if (filters.tradeIds.length > 0) onFilterChange("tradeIds", []);
    if (filters.businessRoleIds.length > 0) onFilterChange("businessRoleIds", []);
    if (filters.tagIds.length > 0) onFilterChange("tagIds", []);
  };

  return (
    <div className="mb-4 flex items-center gap-2">
      <div className="flex-1">
        <SearchInput
          className=""
          onChange={setSearchInput}
          placeholder="Search by event name..."
          value={searchInput}
        />
      </div>

      <Popover>
        <PopoverTrigger asChild>
          <Button className="gap-2" type="button" variant="outline">
            <SlidersHorizontal className="h-4 w-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center rounded-full bg-gray-100 dark:bg-slate-700 text-xs px-1.5 min-w-[1.25rem] h-5">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[320px] p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              Filters
            </h4>
            {activeFilterCount > 0 && (
              <button
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white underline"
                onClick={clearAll}
                type="button"
              >
                Clear all
              </button>
            )}
          </div>

          <MultiSelect
            onChange={(vals) => onFilterChange("eventTypeIds", vals)}
            options={typeOptions}
            placeholder="Event types..."
            value={filters.eventTypeIds}
          />
          <MultiSelect
            onChange={(vals) => onFilterChange("categoryIds", vals)}
            options={categoryOptions}
            placeholder="Categories..."
            value={filters.categoryIds}
          />
          <MultiSelect
            onChange={(vals) => onFilterChange("tradeIds", vals)}
            options={tradeOptions}
            placeholder="Trades..."
            value={filters.tradeIds}
          />
          <MultiSelect
            onChange={(vals) => onFilterChange("businessRoleIds", vals)}
            options={roleOptions}
            placeholder="Roles..."
            value={filters.businessRoleIds}
          />
          <MultiSelect
            onChange={(vals) => onFilterChange("tagIds", vals)}
            options={tagOptions}
            placeholder="Tags..."
            value={filters.tagIds}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default EventFilters;
